import React, {useState} from 'react';
import cx from 'classnames';
import {getInteractionModality} from '@react-aria/interactions';

import {
    Avatar,
    AvatarType,
    Button,
    FontVariant,
    Icon,
    IconTypes,
    RefButton,
    Text,
    ThemeConsumer,
    InteractiveElement,
} from '@pexip/components';

import {TestId} from '../../../test/testIds';

import styles from './CallHistoryItem.module.scss';
import {useTranslation} from 'react-i18next';
import {CallHistoryDirection, KEYBOARD_EVENT_KEY} from '../..';
import {CallHistoryItemMoreMenu} from '../CallHistoryItemMoreMenu/CallHistoryItemMoreMenu.view';
import {toRelativeDateTime} from '../../utils/toRelativeDateTime';

export const CallHistoryItemView: React.FC<{
    title?: string;
    meetingId: string;
    direction?: CallHistoryDirection;
    dateAndTime?: string;
    imageUrl?: string;
    callSucceeded?: boolean;
    isRemoveFavoriteDisabled?: boolean;
    removeFavoriteDisabledReason?: React.ReactNode;
    isMobile: boolean;
    draggableHandleAriaLabel?: string;
    className?: string;
    onClick: () => void;
    handleCallMeetingId: () => void;
    handleDeleteRecent?: () => void;
    handleRemoveFavorite?: () => void;
    handleAddToFavorites?: () => void;
    handleReorderByKeyboard?: (direction: 'up' | 'down') => void;
    setDragCaptureTarget?: React.Dispatch<
        React.SetStateAction<HTMLElement | null>
    >;
}> = ({
    title,
    meetingId,
    direction,
    dateAndTime,
    imageUrl,
    isRemoveFavoriteDisabled = false,
    removeFavoriteDisabledReason,
    callSucceeded,
    isMobile,
    draggableHandleAriaLabel,
    className,
    onClick,
    handleCallMeetingId,
    handleDeleteRecent,
    handleRemoveFavorite,
    handleAddToFavorites,
    handleReorderByKeyboard,
    setDragCaptureTarget,
}) => {
    const {t, i18n} = useTranslation();
    const formattedDateTime = dateAndTime
        ? toRelativeDateTime(new Date(dateAndTime), {
              t,
              language: i18n.language,
          })
        : undefined;
    const relativeFormattedDateTime =
        formattedDateTime?.relativeFormattedDateTime;
    const fullFormattedDateTime = formattedDateTime?.fullFormattedDateTime;
    const [isHovering, setIsHovering] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [keepMenuButtonRendered, setKeepMenuButtonRendered] = useState(false);
    const showMenuButton =
        isMobile || isHovering || hasFocus || isOpen || keepMenuButtonRendered;
    let directionText = '';
    let directionIcon: React.ReactElement | undefined;
    const isDraggable = !!setDragCaptureTarget;

    switch (direction) {
        case CallHistoryDirection.Incoming:
            directionText = t('media.call-history.incoming', 'Incoming');
            directionIcon = (
                <Icon
                    size="compact"
                    className={cx(styles.incomingIcon, {
                        [styles.callSucceeded]: callSucceeded,
                        [styles.callFailed]: !callSucceeded,
                    })}
                    source={IconTypes.IconArrowLeftRegular}
                />
            );
            break;
        case CallHistoryDirection.Outgoing:
            directionText = t('media.call-history.outgoing', 'Outgoing');
            directionIcon = (
                <Icon
                    size="compact"
                    className={cx(styles.outgoingIcon, {
                        [styles.callSucceeded]: callSucceeded,
                        [styles.callFailed]: !callSucceeded,
                    })}
                    source={IconTypes.IconArrowLeftRegular}
                />
            );
            break;
        case CallHistoryDirection.Missed:
            directionText = t('media.call-history.missed', 'Missed');
            directionIcon = (
                <Icon
                    size="compact"
                    className={cx(styles.missedIcon, styles.callFailed)}
                    source={IconTypes.IconArrowCornerDown}
                />
            );
            break;
    }

    const menuButton = (
        <Button
            onClick={() => {
                setIsOpen(!isOpen);
            }}
            aria-label={t(
                'media.call-history.open-menu',
                'Open call history item menu',
            )}
            isActive={isOpen}
            role="button"
            variant={isMobile ? 'transparent' : 'secondary'}
            modifier="square"
            size="small"
            data-testid={TestId.ButtonCallHistoryItemMenu}
        >
            <Icon
                size={isMobile ? 'small' : 'compact'}
                source={IconTypes.IconMoreVertical}
            />
        </Button>
    );

    return (
        <ThemeConsumer>
            {({colorScheme: defaultColorScheme}) => {
                return (
                    <InteractiveElement
                        data-no-drag
                        tabIndex={0}
                        htmlTag="div"
                        onClick={onClick}
                        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                            if (e.key === KEYBOARD_EVENT_KEY.enter) {
                                onClick();
                            }
                        }}
                        className={cx(
                            defaultColorScheme,
                            styles[defaultColorScheme],
                            styles.item,
                            className,
                        )}
                        onMouseEnter={() => setIsHovering(true)}
                        onMouseLeave={() => setIsHovering(false)}
                        onFocus={() => {
                            if (getInteractionModality() !== 'pointer') {
                                setHasFocus(true);
                            }
                        }}
                        onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
                            if (!e.currentTarget.contains(e.relatedTarget)) {
                                setHasFocus(false);
                                if (!isOpen) {
                                    setKeepMenuButtonRendered(false);
                                }
                            }
                        }}
                        data-testid={TestId.CallHistoryItem}
                    >
                        <div className={styles.avatarAndInfo}>
                            <div className={styles.avatarAndDragHandle}>
                                {isDraggable && (
                                    <RefButton
                                        data-drag-handle
                                        aria-label={draggableHandleAriaLabel}
                                        onClick={(
                                            e: React.MouseEvent<HTMLButtonElement>,
                                        ) => {
                                            e.stopPropagation();
                                        }}
                                        onKeyDown={(
                                            e: React.KeyboardEvent<HTMLButtonElement>,
                                        ) => {
                                            if (
                                                e.key ===
                                                    KEYBOARD_EVENT_KEY.arrowUp ||
                                                e.key ===
                                                    KEYBOARD_EVENT_KEY.arrowDown
                                            ) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleReorderByKeyboard?.(
                                                    e.key ===
                                                        KEYBOARD_EVENT_KEY.arrowUp
                                                        ? 'up'
                                                        : 'down',
                                                );
                                            } else if (
                                                e.key ===
                                                KEYBOARD_EVENT_KEY.enter
                                            ) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }
                                        }}
                                        ref={setDragCaptureTarget}
                                        size="none"
                                        variant="transparent"
                                    >
                                        <Icon
                                            source={IconTypes.IconDraggable}
                                            size="compact"
                                        />
                                    </RefButton>
                                )}
                                <Avatar
                                    className={styles.avatar}
                                    sizeModifier="md"
                                    imageUrl={imageUrl}
                                    type={
                                        imageUrl
                                            ? AvatarType.Image
                                            : AvatarType.Initials
                                    }
                                    displayName={title ?? meetingId}
                                />
                            </div>
                            <div className={styles.info}>
                                <Text
                                    title={title ?? meetingId}
                                    fontVariant={FontVariant.Body}
                                >
                                    {title ?? meetingId}
                                </Text>
                                {title && meetingId && (
                                    <Text
                                        title={meetingId}
                                        fontVariant={
                                            isMobile
                                                ? FontVariant.Body
                                                : FontVariant.Small
                                        }
                                    >
                                        {meetingId}
                                    </Text>
                                )}
                                {direction && formattedDateTime && (
                                    <Text
                                        className={
                                            styles.directionAndDateContainer
                                        }
                                        title={`${directionText} - ${fullFormattedDateTime}`}
                                        fontVariant={
                                            isMobile
                                                ? FontVariant.Body
                                                : FontVariant.Small
                                        }
                                    >
                                        {directionIcon}
                                        {directionText}{' '}
                                        <span
                                            className={
                                                styles.directionAndDateDivider
                                            }
                                        />{' '}
                                        {relativeFormattedDateTime}
                                    </Text>
                                )}
                            </div>
                        </div>
                        <div className={styles.menuContainer}>
                            {showMenuButton && (
                                <CallHistoryItemMoreMenu
                                    isTouchDevice={isMobile}
                                    title={title}
                                    meetingId={meetingId}
                                    button={menuButton}
                                    isRemoveFavoriteDisabled={
                                        isRemoveFavoriteDisabled
                                    }
                                    removeFavoriteDisabledReason={
                                        removeFavoriteDisabledReason
                                    }
                                    onOpenChange={state => {
                                        setIsOpen(state);
                                        if (!state) {
                                            const dismissedByPointer =
                                                getInteractionModality() ===
                                                'pointer';
                                            setKeepMenuButtonRendered(
                                                !dismissedByPointer,
                                            );
                                            if (dismissedByPointer) {
                                                setHasFocus(false);
                                            }
                                        }
                                    }}
                                    handleCallMeetingId={handleCallMeetingId}
                                    handleDeleteRecent={handleDeleteRecent}
                                    handleRemoveFavorite={handleRemoveFavorite}
                                    handleAddToFavorites={handleAddToFavorites}
                                />
                            )}
                        </div>
                    </InteractiveElement>
                );
            }}
        </ThemeConsumer>
    );
};

export type CallHistoryItemProps = React.ComponentProps<
    typeof CallHistoryItemView
>;
